import { MODEL_NUMBER_COLUMN_WEIGHTS } from "./TableModelNumbers.constants"

export type TModelNumberColumnKey = keyof typeof MODEL_NUMBER_COLUMN_WEIGHTS

export type TResolvedColumnWidths = {
	widths: Record<TModelNumberColumnKey, number>
	total: number
}

const COLUMN_KEYS = Object.keys(MODEL_NUMBER_COLUMN_WEIGHTS) as TModelNumberColumnKey[]

/**
 * Turn the proportional column weights into pixel widths that add up to exactly
 * `containerWidth`.
 *
 * Virtualization needs both: antd only switches to its virtual body when every column
 * has a numeric width and `scroll.x` is numeric too. Deriving them from the measured
 * container rather than hardcoding them is what keeps the table filling its space at any
 * window size — a fixed pixel total either overflows the page (the sidebar gets cut off)
 * or leaves dead space to the right of the last column.
 *
 * Rounding is absorbed by the last column so the parts always sum to the whole; handing
 * antd a `scroll.x` that disagrees with the column widths by even a pixel puts a
 * permanent horizontal scrollbar under the table.
 */
export const resolveColumnWidths = (containerWidth: number): TResolvedColumnWidths => {
	const totalWeight = COLUMN_KEYS.reduce((sum, key) => sum + MODEL_NUMBER_COLUMN_WEIGHTS[key], 0)
	const widths = {} as Record<TModelNumberColumnKey, number>

	let assigned = 0
	COLUMN_KEYS.forEach((key, index) => {
		const isLast = index === COLUMN_KEYS.length - 1
		const width = isLast
			? containerWidth - assigned
			: Math.round((MODEL_NUMBER_COLUMN_WEIGHTS[key] / totalWeight) * containerWidth)
		widths[key] = width
		assigned += width
	})

	return { widths, total: containerWidth }
}
